import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import { MdDelete, MdPersonAdd, MdVpnKey } from 'react-icons/md';

const UserManagement = () => {
    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [requests, setRequests] = useState([]);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [newUserData, setNewUserData] = useState({
        username: '',
        password: '',
        email: '',
        name: '',
        role: 'User'
    });

    useEffect(() => {
        if (activeTab === 'users') {
            fetchUsers();
        } else {
            fetchRequests();
        }
    }, [activeTab]);

    const fetchUsers = async () => {
        try {
            const response = await axios.get('login/users/');
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users", error);
        }
    };

    const fetchRequests = async () => {
        try {
            const response = await axios.get('login/admin-password-reset/');
            setRequests(response.data);
        } catch (error) {
            console.error("Error fetching requests", error);
        }
    };

    const handleDeleteUser = async (id) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        try {
            await axios.delete(`login/users/${id}/`);
            fetchUsers();
        } catch (error) {
            alert("Failed to delete user: " + (error.response?.data?.message || error.message));
        }
    };

    const handleToggleActive = async (user) => {
        const action = user.is_active ? "deactivate" : "activate";
        if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

        try {
            await axios.patch(`login/users/${user.id}/`, { is_active: !user.is_active });
            fetchUsers();
            alert(`User ${action}d successfully`);
        } catch (error) {
             alert(`Failed to ${action} user: ` + (error.response?.data?.message || error.message));
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            await axios.post('login/users/', newUserData);
            setShowAddUserModal(false);
            setNewUserData({ username: '', password: '', email: '', name: '', role: 'User' });
            fetchUsers();
            alert("User created successfully");
        } catch (error) {
             alert("Failed to create user: " + (error.response?.data?.message || error.message));
        }
    };
    
    const handleSendLink = async (requestId) => {
        try {
             const res = await axios.post('login/admin-password-reset/', { request_id: requestId });
             fetchRequests();
             alert(res.data?.message || "Password reset link sent via email.");
        } catch (error) {
             alert("Failed to send link: " + (error.response?.data?.message || error.message));
        }
    };

    const handleSendUserResetLink = async (user) => {
        if (!window.confirm(`Send password reset link to ${user.name || user.username} (${user.email || 'No email'})?`)) return;
        try {
             const res = await axios.post('login/admin-password-reset/', { user_id: user.id });
             alert(res.data?.message || `Password reset link sent to ${user.email}`);
        } catch (error) {
             alert("Failed to send reset link: " + (error.response?.data?.message || error.message));
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'users' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Users
                    </button>
                    <button
                        onClick={() => setActiveTab('requests')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'requests' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Password Reset Requests
                    </button>
                </div>
            </div>

            {activeTab === 'users' && (
                <div>
                    <div className="flex justify-end mb-4">
                        <button
                            onClick={() => setShowAddUserModal(true)}
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            <MdPersonAdd className="mr-2" /> Add User
                        </button>
                    </div>
                    <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.username}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                                ${user.role === 'Super Admin' ? 'bg-purple-100 text-purple-800' : 
                                                  user.role === 'Admin' ? 'bg-blue-100 text-blue-800' : 
                                                  user.role === 'Manager' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                                {user.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            {user.role !== 'Super Admin' && (
                                                <button 
                                                    onClick={() => handleToggleActive(user)} 
                                                    className={`${user.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'} font-bold mr-2 text-xs`}
                                                >
                                                    {user.is_active ? 'Deactivate' : 'Activate'}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleSendUserResetLink(user)}
                                                className="px-2.5 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded text-xs font-semibold inline-flex items-center gap-1 transition-colors mr-2"
                                                title="Send Password Reset Link via Email"
                                            >
                                                <MdVpnKey className="w-3.5 h-3.5" /> Send Reset Link
                                            </button>
                                            {user.role !== 'Super Admin' && (
                                                <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-900" title="Delete User">
                                                    <MdDelete className="w-5 h-5 inline" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'requests' && (
                 <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested At</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                         <tbody className="bg-white divide-y divide-gray-200">
                            {requests.length === 0 ? (
                                <tr><td colSpan="3" className="px-6 py-4 text-center text-gray-500">No pending requests</td></tr>
                            ) : (
                                requests.map((req) => (
                                    <tr key={req.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{req.username}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(req.created_at).toLocaleString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                             <button 
                                                onClick={() => handleSendLink(req.id)}
                                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                                             >
                                                Send Link
                                             </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                         </tbody>
                    </table>
                 </div>
            )}

            {/* Add User Modal */}
            {showAddUserModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                         <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowAddUserModal(false)}></div>
                         <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <form onSubmit={handleAddUser}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                     <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Add New User</h3>
                                     <div className="space-y-4">
                                         <input type="text" placeholder="Full Name" required className="w-full border p-2 rounded" 
                                            value={newUserData.name} onChange={e => setNewUserData({...newUserData, name: e.target.value})} />
                                         <input type="text" placeholder="Username" required className="w-full border p-2 rounded" 
                                            value={newUserData.username} onChange={e => setNewUserData({...newUserData, username: e.target.value})} />
                                         <input type="email" placeholder="Email" required className="w-full border p-2 rounded" 
                                            value={newUserData.email} onChange={e => setNewUserData({...newUserData, email: e.target.value})} />
                                         <input type="password" placeholder="Password" required className="w-full border p-2 rounded" 
                                            value={newUserData.password} onChange={e => setNewUserData({...newUserData, password: e.target.value})} />
                                         <select className="w-full border p-2 rounded" value={newUserData.role} onChange={e => setNewUserData({...newUserData, role: e.target.value})}>
                                             <option value="User">User</option>
                                             <option value="Staff">Staff</option>
                                             <option value="Manager">Manager</option>
                                             <option value="Admin">Admin</option>
                                         </select>
                                     </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm">
                                        Create User
                                    </button>
                                    <button type="button" onClick={() => setShowAddUserModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                         </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
